import React, { useEffect } from 'react';
import useFetch from 'use-http';
import Moment from 'react-moment';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { SlashIcon } from 'lucide-react';

import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

import { useCartState } from '@/components/context/CartContext';

import InstrumentalResearchDocumentsCard from './InstrumentalResearchDocumentsCard';

const InstrumentalResearcheDetails = () => {
  const history = useHistory();
  const { id } = useParams();

  const { get: getResearch } = useFetch('/UserAccount/InstrumentalResearch');

  const cartState = useCartState();

  const [loading, setLoading] = React.useState(false);
  const [research, setResearch] = React.useState(
    history.location.state?.data || null,
  );

  // 1:1: keep this exactly where it was, with the same behavior.
  const [leftToPaysera, setLeftToPaysera] = React.useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const res = await getResearch(`?id=${id}`);
        setResearch(res);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // 1:1: original cancel redirect logic
  useEffect(() => {
    if (cartState?.registrationId && !leftToPaysera && !id) {
      history.push('/payment/status/cancel');
    }
  }, [cartState]);

  const formatDate = (dateStr) =>
    new Date(Date.parse(dateStr)).getUTCHours() === 0 ? (
      <Moment utc format="YYYY-MM-DD">
        {dateStr}
      </Moment>
    ) : (
      <Moment utc format="YYYY-MM-DD HH:mm">
        {dateStr}
      </Moment>
    );

  if (loading || !research) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full"></div>
        <p className="mt-4 text-gray-600">Laukiama informacijos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Breadcrumb className="px-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/userAccount/instrumental-researches">
                Instrumentiniai tyrimai
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <SlashIcon />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{research.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Card 1: Text + Observations */}
      <Card>
        <CardHeader>
          <Heading variant="userAccountDetailsTitle">{research.name}</Heading>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <span>Data: </span>
            <span>{formatDate(research.orderDate)}</span>
          </div>

          {research.statusDisplay && (
            <div>
              <span>Būsena: </span>
              <span>{research.statusDisplay}</span>
            </div>
          )}

          {research.statusDisplay !== 'Užbaigtas' &&
            research.statusDisplay !== 'Neatliktas' && (
              <div className="text-red-600 font-medium">LAUKIAMA REZULTATŲ</div>
            )}

          {/* Observations */}
          {research.observations?.length > 0 && (
            <>
              <div className="pt-4 font-semibold uppercase text-sm">
                Instrumentinių tyrimų vertinimai:
              </div>

              {research.observations.map((result, index) => (
                <div key={index} className="space-y-2 pt-2">
                  <div>
                    <span>Data: </span>
                    <span>{formatDate(result.date)}</span>
                  </div>
                  <div>
                    <span>Dokumento numeris: </span>
                    <span>{result.documentNumber}</span>
                  </div>
                  <div>
                    <span>Radiologo vardas: </span>
                    <span>{result.radiologist?.name}</span>
                  </div>
                  <div>
                    <span>Radiologo pavardė: </span>
                    <span>{result.radiologist?.surname}</span>
                  </div>
                  <div>
                    <span>Radiologo technologo vardas: </span>
                    <span>{result.radiologyTechnologist?.name}</span>
                  </div>
                  <div>
                    <span>Radiologo technologo pavardė: </span>
                    <span>{result.radiologyTechnologist?.surname}</span>
                  </div>

                  {result.value && (
                    <div>
                      <span>Išvados:</span>
                      <p>{result.value}</p>
                    </div>
                  )}
                  {result.description && (
                    <div>
                      <span>Aprašymas:</span>
                      <p>{result.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </CardContent>
        <CardFooter />
      </Card>

      {/* Card 2: Dokumentai (1:1 extracted, reusable) */}
      <InstrumentalResearchDocumentsCard
        research={research}
        setLeftToPaysera={setLeftToPaysera}
        component={"instrumental"}
      />
    </div>
  );
};

export default InstrumentalResearcheDetails;
